// ============================================================
// 项目部署·统一高清出图（DW-P0-7）
// - scene：复用 C2 抓取注册表（分辨率档位/视角/格式，SceneCanvas 内注册）
// - dashboard / fusion：html2canvas 高清合成（WebGL 底 preserveDrawingBuffer 支持直读）
// - 水印：复用 stampWatermark（免费/临时档由调用方按 meta/policy 传入）
// 供项目部署对话框「导出图片」/预览/发布复用；服务端仅做轻量记录与额度（kind='image'）
// ============================================================
import html2canvas from 'html2canvas';
import { sceneImageExportRegistry } from '../stores/sceneImageExportStore';
import { buildExportFilename, downloadDataUrl, stampWatermark } from './imageExport';

export type DeployImageFormat = 'png' | 'jpg';
export type DeployImageScale = 1 | 2 | 4;
export type DeployImageKind = 'scene' | 'dashboard' | 'fusion';

export interface DeployImageOptions {
  name?: string;
  format?: DeployImageFormat;
  scale?: DeployImageScale;
  /** jpg 质量 0-1，默认 0.92 */
  quality?: number;
  /** 传非空值即叠加水印（免费/临时档） */
  watermark?: { text?: string; logoUrl?: string } | null;
  /** 3D/融合视角（可选，缺省当前视角） */
  view?: { position: [number, number, number]; target: [number, number, number] };
  designWidth?: number;
  designHeight?: number;
}

export interface DeployImageResult {
  dataUrl: string;
  filename: string;
  width: number;
  height: number;
}

/** 档位 → 目标宽度（与编辑器出图预设一致：1080p/2K/4K） */
export const EXPORT_WIDTH_BY_SCALE: Record<DeployImageScale, number> = { 1: 1920, 2: 2560, 4: 3840 };

export function resolveExportSize(
  designWidth: number | undefined,
  designHeight: number | undefined,
  scale: DeployImageScale | undefined,
): { width: number; height: number } {
  const s: DeployImageScale = scale === 2 || scale === 4 ? scale : 1;
  const dw = Math.max(1, Math.trunc(Number(designWidth) || 1920));
  const dh = Math.max(1, Math.trunc(Number(designHeight) || 1080));
  const width = EXPORT_WIDTH_BY_SCALE[s];
  return { width, height: Math.max(1, Math.round((dh / dw) * width)) };
}

const mimeOf = (f: DeployImageFormat) => (f === 'jpg' ? 'image/jpeg' : 'image/png');
const fmtArg = (f: DeployImageFormat): 'png' | 'jpeg' => (f === 'jpg' ? 'jpeg' : 'png');

/** 3D 单图（当前/指定视角）：复用 SceneCanvas 抓取器 */
export function captureSceneImage(opts: DeployImageOptions = {}): DeployImageResult {
  const format: DeployImageFormat = opts.format === 'jpg' ? 'jpg' : 'png';
  const { width, height } = resolveExportSize(opts.designWidth, opts.designHeight, opts.scale);
  const capture = sceneImageExportRegistry.capture;
  if (!capture) throw new Error('当前没有可导出的 3D 画布（请在 3D 场景/融合视图中使用）');
  const dataUrl = capture({
    width,
    height,
    format: fmtArg(format),
    quality: opts.quality ?? 0.92,
    ...(opts.view ? { view: opts.view } : {}),
  });
  if (!dataUrl) throw new Error('导出失败：WebGL 上下文不可用');
  return { dataUrl, width, height, filename: buildExportFilename(opts.name ?? '场景', width, height, fmtArg(format)) };
}

/** 2D 大屏 / 融合：html2canvas 按目标分辨率倍率抓取（融合容器含 WebGL 底，直读） */
async function captureDomImage(element: HTMLElement, opts: DeployImageOptions): Promise<DeployImageResult> {
  const format: DeployImageFormat = opts.format === 'jpg' ? 'jpg' : 'png';
  // 画布容器可能被宿主 transform 缩放：按未变换布局尺寸(offsetWidth/Height)做裁切窗口，
  // scale 倍率按目标宽/布局宽计算，保证输出=设计分辨率的整数倍（与编辑器出图预设一致）
  const cssW = Math.max(1, element.offsetWidth || element.clientWidth || 1920);
  const cssH = Math.max(1, element.offsetHeight || element.clientHeight || 1080);
  const designWidth = Number(opts.designWidth) || cssW;
  const designHeight = Number(opts.designHeight) || cssH;
  const { width } = resolveExportSize(designWidth, designHeight, opts.scale);
  const hit = Math.max(1, width / cssW);
  const canvas = await html2canvas(element, {
    scale: hit,
    width: cssW,
    height: cssH,
    backgroundColor: format === 'jpg' ? '#0a0a1a' : null,
    useCORS: true,
    logging: false,
  });
  const dataUrl = canvas.toDataURL(mimeOf(format), opts.quality ?? 0.92);
  return {
    dataUrl,
    width: canvas.width,
    height: canvas.height,
    filename: buildExportFilename(opts.name ?? '大屏', canvas.width, canvas.height, fmtArg(format)),
  };
}

export const captureDashboardImage = (element: HTMLElement, opts: DeployImageOptions = {}) =>
  captureDomImage(element, opts);
export const captureFusionImage = (element: HTMLElement, opts: DeployImageOptions = {}) =>
  captureDomImage(element, opts);

/** 统一入口：按产物类型抓图 + 可选水印；返回 dataURL 与文件名 */
export async function exportDeployImage(
  kind: DeployImageKind,
  target: HTMLElement | null | undefined,
  opts: DeployImageOptions = {},
): Promise<DeployImageResult> {
  let result: DeployImageResult;
  if (kind === 'scene') {
    result = captureSceneImage(opts);
  } else if (kind === 'fusion') {
    if (!target) throw new Error('缺少融合视图容器');
    result = await captureFusionImage(target, opts);
  } else {
    if (!target) throw new Error('缺少大屏画布容器');
    result = await captureDashboardImage(target, opts);
  }
  if (opts.watermark && result.dataUrl) {
    result = { ...result, dataUrl: await stampWatermark(result.dataUrl, opts.watermark).catch(() => result.dataUrl) };
  }
  return result;
}

export { downloadDataUrl };

// 开发/测试桥（e2e 用）：真实浏览器内直接调用导出服务
if (import.meta.env?.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__deployImageExport = {
    exportDeployImage,
    resolveExportSize,
    EXPORT_WIDTH_BY_SCALE,
  };
}
